import type { HTMLAttributes, ReactNode } from 'react';
import styles from './AppBanner.module.css';

export type AppBannerVariant = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export interface AppBannerProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AppBannerVariant;
  title?: string;
  text: string;
  compact?: boolean;
  actions?: ReactNode;
}

function BannerIcon({ variant }: { variant: AppBannerVariant }) {
  if (variant === 'success') {
    return (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M20 6 9 17l-5-5"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (variant === 'warning') {
    return (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (variant === 'danger') {
    return (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v5m0 3h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (variant === 'neutral') {
    return (
      <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 16v-4m0-4h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg className={styles.icon} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 16v-4m0-4h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function defaultRole(variant: AppBannerVariant): 'status' | 'alert' {
  return variant === 'danger' || variant === 'warning' ? 'alert' : 'status';
}

export default function AppBanner({
  variant = 'info',
  title,
  text,
  compact = false,
  actions,
  className = '',
  role,
  ...rest
}: AppBannerProps) {
  const hasTitle = Boolean(title?.trim());

  return (
    <div
      className={`${styles.banner} ${styles[variant]} ${compact ? styles.compact : ''} ${className}`.trim()}
      role={role ?? defaultRole(variant)}
      {...rest}
    >
      <span className={styles.iconWrap} aria-hidden>
        <BannerIcon variant={variant} />
      </span>
      <div className={styles.content}>
        {hasTitle ? <p className={styles.title}>{title}</p> : null}
        <p className={styles.text}>{text}</p>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    </div>
  );
}
